// Session manager for both build modes (src/lib/identity.ts).
//
// Demo (VITE_PTW_MODE=synthetic_demo): every persona is a separate Supabase anonymous
// sign-in, bound on the server to one fictional person (public.ptw_demo_bind). Each persona
// keeps its own client and token store, so a signature is always made by that persona's
// own session - the server never takes a "who am I" claim from the browser. Switching
// persona switches which session sends the next request.
//
// Accounts (operational / staging): one client and one person per browser. The person
// asks for a one-time code at their invited email address, enters it, and
// public.ptw_account_activate binds that sign-in to their invited account on first use
// (0022). There is no persona switching and no anonymous sign-in. Signing out forgets the
// session, this person's cached data and their local draft copies.
import { createClient, type SupabaseClient } from '@supabase/supabase-js'
import type { DemoPersona } from '@/domain/types'
import { env, isConfigured } from './env'
import {
  type AccountIdentity,
  activationProblem,
  authErrorProblem,
  identityKey,
  isAccountMode,
  normaliseCode,
  normaliseEmail,
  remainingMs,
  SIGN_IN_LIMIT_MS as LIMIT,
  SIGN_IN_WARNING_MS as WARNING,
  SignInError,
} from './identity'

const KEY_SANDBOX = 'ptw-demo:sandbox'
const KEY_ACTIVE = 'ptw-demo:active'
const KEY_BOUND = 'ptw-demo:bound' // JSON array of persona codes bound in this browser
const KEY_SIGNED_IN = 'ptw-demo:signed-in-at:' // + persona code: when that persona last signed in (ISO)

const KEY_ACCOUNT = 'ptw-account:identity' // JSON AccountIdentity of the signed-in person
const ACCOUNT_AUTH_STORAGE = 'ptw-account:auth'

/**
 * A sign-in lasts one hour, whatever the person is doing (Razan, 2026-11-04). The Supabase
 * project enforces the same limit (auth sessions_timebox = 3600 s, see
 * scripts/setup/supabase-project.mjs), so an old token stops working even if this check
 * were bypassed. After the hour the person must sign in again before anything is sent.
 */
export const SIGN_IN_LIMIT_MS = LIMIT
/** The warning appears this long before the end. */
export const SIGN_IN_WARNING_MS = WARNING

const accountMode = isAccountMode(env.mode)

type Listener = () => void

const clients = new Map<string, SupabaseClient>()
const listeners = new Set<Listener>()

function read(key: string): string | null {
  try {
    return window.localStorage.getItem(key)
  } catch {
    return null
  }
}

function write(key: string, value: string | null): void {
  try {
    if (value === null) window.localStorage.removeItem(key)
    else window.localStorage.setItem(key, value)
  } catch {
    /* storage unavailable: session lasts for this tab only */
  }
}

function removeWithPrefix(prefix: string): void {
  try {
    for (let i = window.localStorage.length - 1; i >= 0; i--) {
      const k = window.localStorage.key(i)
      if (k && k.startsWith(prefix)) window.localStorage.removeItem(k)
    }
  } catch {
    /* ignore */
  }
}

function boundCodes(): string[] {
  try {
    return JSON.parse(read(KEY_BOUND) ?? '[]') as string[]
  } catch {
    return []
  }
}

function setBound(codes: string[]): void {
  write(KEY_BOUND, JSON.stringify([...new Set(codes)]))
}

function readAccount(): AccountIdentity | null {
  try {
    const v = JSON.parse(read(KEY_ACCOUNT) ?? 'null') as AccountIdentity | null
    return v && typeof v.userId === 'string' && typeof v.email === 'string' && typeof v.signedInAt === 'number' ? v : null
  } catch {
    return null
  }
}

/** When this persona last signed in here, or null if never (or unknown). */
export function signedInAt(personaCode: string | null): number | null {
  if (!personaCode) return null
  const v = read(KEY_SIGNED_IN + personaCode)
  const t = v ? Date.parse(v) : NaN
  return Number.isFinite(t) ? t : null
}

function markSignedIn(personaCode: string): void {
  write(KEY_SIGNED_IN + personaCode, new Date().toISOString())
}

/**
 * Milliseconds left on the current sign-in (<= 0 once it has ended). Demo: the given (or
 * active) persona's; accounts: the signed-in person's. No recorded sign-in time counts as
 * ended, so it is asked to sign in again rather than kept on indefinitely.
 */
export function signInRemainingMs(personaCode: string | null = snapshot.activePersona, now: number = Date.now()): number {
  if (accountMode) return remainingMs(snapshot.account?.signedInAt ?? null, now)
  return remainingMs(signedInAt(personaCode), now)
}

export class SignInExpiredError extends Error {
  readonly code = 'SIGN_IN_EXPIRED'
  constructor() {
    super('Your sign-in has ended after one hour. Sign in again to continue.')
  }
}

function notify(): void {
  for (const l of listeners) l()
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export type SessionSnapshot = {
  /** Account mode: real people; never a persona. */
  accountMode: boolean
  sandboxCode: string | null
  activePersona: string | null
  boundPersonas: string[]
  /** The signed-in person (account mode only). */
  account: AccountIdentity | null
  /** Cache/storage identity: sandbox:persona in the demo, user:<auth user id> for accounts; null signed out. */
  identityKey: string | null
}

let snapshot: SessionSnapshot = computeSnapshot()

function computeSnapshot(): SessionSnapshot {
  const base = accountMode
    ? { sandboxCode: null, activePersona: null, boundPersonas: [] as string[], account: readAccount() }
    : { sandboxCode: read(KEY_SANDBOX), activePersona: read(KEY_ACTIVE), boundPersonas: boundCodes(), account: null }
  return { accountMode, ...base, identityKey: identityKey({ mode: env.mode, ...base }) }
}

function refreshSnapshot(): void {
  snapshot = computeSnapshot()
  notify()
}

/** Stable snapshot for useSyncExternalStore. */
export function getSnapshot(): SessionSnapshot {
  return snapshot
}

/** The Supabase client for one persona (created on first use, own token store). */
export function clientFor(personaCode: string): SupabaseClient {
  const existing = clients.get(personaCode)
  if (existing) return existing
  if (!isConfigured) throw new Error('Supabase is not configured for this build.')
  const client = createClient(env.supabaseUrl, env.supabaseAnonKey, {
    auth: {
      storageKey: `ptw-demo:auth:${personaCode}`,
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: false,
    },
  })
  clients.set(personaCode, client)
  return client
}

let accountClient: SupabaseClient | null = null

/** The one client of an account build (its own token store; no URL-borne sessions). */
function accountClientInstance(): SupabaseClient {
  if (accountClient) return accountClient
  if (!isConfigured) throw new Error('Supabase is not configured for this build.')
  accountClient = createClient(env.supabaseUrl, env.supabaseAnonKey, {
    auth: { storageKey: ACCOUNT_AUTH_STORAGE, persistSession: true, autoRefreshToken: true, detectSessionInUrl: false },
  })
  // Signed out elsewhere (another tab, a revoked refresh token): forget the person here too.
  accountClient.auth.onAuthStateChange((event) => {
    if (event === 'SIGNED_OUT' && snapshot.account) forgetAccount()
  })
  return accountClient
}

export function activeClient(): SupabaseClient | null {
  if (!isConfigured) return null
  if (accountMode) return snapshot.account ? accountClientInstance() : null
  const code = snapshot.activePersona
  if (!code) return null
  return clientFor(code)
}

export function requireActiveClient(): SupabaseClient {
  const c = activeClient()
  if (!c) {
    throw new Error(accountMode ? 'You are not signed in. Sign in with your email code first.' : 'No active persona session. Start or join the synthetic demo first.')
  }
  // Nothing is sent once the hour is up: the person must sign in again first.
  if (signInRemainingMs() <= 0) throw new SignInExpiredError()
  return c
}

/**
 * Demo: signs the active persona in again after the hour (a fresh session bound to the
 * same fictional person). Accounts cannot be signed in again silently: the person asks for
 * a new code (the expiry screen and banners show the code form instead of calling this).
 */
export async function signInAgain(): Promise<void> {
  if (accountMode) throw new Error('Ask for a new sign-in code to continue.')
  const code = snapshot.activePersona
  if (!code) throw new Error('No active persona to sign in again.')
  await switchPersona(code, snapshot.sandboxCode ?? undefined, { force: true })
}

// -----------------------------------------------------------------------------
// Accounts: email one-time code (operational / staging)
// -----------------------------------------------------------------------------

/**
 * Emails a one-time code to the address. The Supabase project creates an auth user only
 * for an invited address (0022 before-user-created hook), so an uninvited address is told
 * so here and no code is sent.
 */
export async function requestSignInCode(email: string): Promise<void> {
  if (!accountMode) throw new Error('Email sign-in is not used in the synthetic demonstration.')
  const client = accountClientInstance()
  const { error } = await client.auth.signInWithOtp({ email: normaliseEmail(email), options: { shouldCreateUser: true } })
  if (error) throw new SignInError(authErrorProblem(error))
}

export type AccountActivation = { status: string; account_id?: string; activated?: boolean }

/**
 * Verifies the code, then asks the server which account this sign-in is (binding it to the
 * invited account on first sign-in). Anything but an active account signs out again and
 * throws the reason. On success the one-hour sign-in starts now.
 */
export async function verifySignInCode(email: string, code: string): Promise<AccountActivation> {
  if (!accountMode) throw new Error('Email sign-in is not used in the synthetic demonstration.')
  const client = accountClientInstance()
  const address = normaliseEmail(email)
  const { data, error } = await client.auth.verifyOtp({ email: address, token: normaliseCode(code), type: 'email' })
  if (error || !data.session || !data.user) throw new SignInError(error ? authErrorProblem(error) : 'CODE_INVALID')
  const res = await client.rpc('ptw_account_activate')
  if (res.error) {
    await client.auth.signOut({ scope: 'local' }).catch(() => undefined)
    throw new SignInError(authErrorProblem(res.error))
  }
  const activation = res.data as AccountActivation
  const problem = activationProblem(activation.status)
  if (problem) {
    await client.auth.signOut({ scope: 'local' }).catch(() => undefined)
    throw new SignInError(problem)
  }
  const previous = snapshot.account
  // Another person signing in on this device starts from nothing of the previous person's.
  if (previous && previous.userId !== data.user.id) removeWithPrefix(draftPrefixFor(previous.userId))
  write(KEY_ACCOUNT, JSON.stringify({ userId: data.user.id, email: address, signedInAt: Date.now() } satisfies AccountIdentity))
  refreshSnapshot()
  return activation
}

/** Local draft copies of an account (src/features/application/model.ts backupKey). */
function draftPrefixFor(userId: string): string {
  return `ptw-demo:draft-copy:-:user:${userId}:`
}

function forgetAccount(): void {
  const previous = snapshot.account
  if (previous) removeWithPrefix(draftPrefixFor(previous.userId))
  write(KEY_ACCOUNT, null)
  removeWithPrefix(ACCOUNT_AUTH_STORAGE)
  refreshSnapshot()
}

/** Signs the person out on this device and forgets their session and local draft copies. */
export async function signOutAccount(): Promise<void> {
  if (accountClient) await accountClient.auth.signOut({ scope: 'local' }).catch(() => undefined)
  forgetAccount()
}

/**
 * On start-up: a remembered person whose stored session is gone (signed out elsewhere,
 * storage cleared) is forgotten, so the app asks for a code instead of failing calls.
 */
export async function checkAccountSession(): Promise<void> {
  if (!accountMode || !isConfigured || !snapshot.account) return
  const { data } = await accountClientInstance().auth.getSession()
  if (!data.session || data.session.user.id !== snapshot.account.userId) forgetAccount()
}

// -----------------------------------------------------------------------------
// Demo personas (synthetic_demo)
// -----------------------------------------------------------------------------

async function ensureSignedIn(client: SupabaseClient): Promise<void> {
  const { data } = await client.auth.getSession()
  if (data.session) return
  const { error } = await client.auth.signInAnonymously()
  if (error) throw error
}

function requireDemo(): void {
  if (accountMode) throw new Error('Demo personas exist only in the synthetic demonstration build.')
}

export type DemoResponse = { sandbox_code: string; personas: DemoPersona[]; bound: { persona_code: string } }
export type DemoStatusResponse = { sandbox_code: string; created_at: string; personas: DemoPersona[] }

/** Creates a fresh sandbox and signs in as the first persona. */
export async function startDemo(personaCode = 'PA'): Promise<DemoResponse> {
  requireDemo()
  await leaveDemo()
  const client = clientFor(personaCode)
  await ensureSignedIn(client)
  const { data, error } = await client.rpc('ptw_demo_start', { p_persona_code: personaCode })
  if (error) throw error
  const res = data as DemoResponse
  write(KEY_SANDBOX, res.sandbox_code)
  write(KEY_ACTIVE, personaCode)
  setBound([personaCode])
  markSignedIn(personaCode)
  refreshSnapshot()
  return res
}

/**
 * Switches to a persona, binding a fresh anonymous session for it on first use.
 * force: re-bind even if this browser bound it before (e.g. the persona was moved to
 * another device, which disabled this browser's account for it).
 */
export async function switchPersona(personaCode: string, sandboxCode?: string, opts: { force?: boolean } = {}): Promise<void> {
  requireDemo()
  const sandbox = sandboxCode ?? snapshot.sandboxCode
  if (!sandbox) throw new Error('No demo sandbox. Start or join one first.')
  let client = clientFor(personaCode)
  if (opts.force) {
    try {
      await client.auth.signOut({ scope: 'local' })
    } catch {
      /* already signed out */
    }
    clients.delete(personaCode)
    client = clientFor(personaCode)
  }
  const alreadyBound = !opts.force && boundCodes().includes(personaCode) && sandbox === snapshot.sandboxCode
  const { data } = await client.auth.getSession()
  // A persona whose hour is up is never silently signed in again by switching to it:
  // it must go through signInAgain (force), which the expired screen offers.
  if (alreadyBound && signInRemainingMs(personaCode) <= 0) {
    write(KEY_SANDBOX, sandbox)
    write(KEY_ACTIVE, personaCode)
    refreshSnapshot()
    return
  }
  if (!alreadyBound || !data.session) {
    await ensureSignedIn(client)
    const { error } = await client.rpc('ptw_demo_bind', { p_sandbox_code: sandbox, p_persona_code: personaCode })
    if (error) throw error
    setBound(sandbox === snapshot.sandboxCode ? [...boundCodes(), personaCode] : [personaCode])
    markSignedIn(personaCode)
  }
  write(KEY_SANDBOX, sandbox)
  write(KEY_ACTIVE, personaCode)
  refreshSnapshot()
}

/** Joins an existing sandbox (another device) as a persona. Re-binding moves that persona here. */
export async function joinSandbox(sandboxCode: string, personaCode: string): Promise<void> {
  requireDemo()
  const code = sandboxCode.trim().toUpperCase()
  if (code !== snapshot.sandboxCode) {
    await leaveDemo()
    write(KEY_SANDBOX, code)
    refreshSnapshot()
  }
  await switchPersona(personaCode, code)
}

export async function demoStatus(): Promise<DemoStatusResponse | null> {
  if (accountMode) return null
  const sandbox = snapshot.sandboxCode
  const client = activeClient()
  if (!sandbox || !client) return null
  const { data, error } = await client.rpc('ptw_demo_status', { p_sandbox_code: sandbox })
  if (error) throw error
  return data as DemoStatusResponse
}

/** Signs every persona out and forgets the sandbox in this browser. */
export async function leaveDemo(): Promise<void> {
  for (const [code, client] of clients) {
    try {
      await client.auth.signOut({ scope: 'local' })
    } catch {
      /* already signed out */
    }
    clients.delete(code)
  }
  removeWithPrefix('ptw-demo:')
  refreshSnapshot()
}
